/**
 * smc/orderBlocks.ts
 * Order block identification.
 */
import { Candle, Displacement, OrderBlock } from '../../../../types';
import { SMCThresholds } from './types';

/**
 * Order Block: the last OPPOSING candle before a displacement move.
 *
 * Bullish OB: last bearish candle before a strong bullish displacement.
 * Bearish OB: last bullish candle before a strong bearish displacement.
 *
 * The OB zone (high-low of that candle) acts as institutional demand/supply.
 * Mitigated = price has traded back through the midpoint of the OB.
 */
export function detectOrderBlocks(
  candles: Candle[],
  displacements: Displacement[],
  thresholds: SMCThresholds
): OrderBlock[] {
  const obs: OrderBlock[] = [];

  for (const disp of displacements) {
    const i = disp.candleIdx;
    if (i < 1) continue;

    if (disp.dir === 'bullish') {
      for (let j = i - 1; j >= Math.max(0, i - 8); j--) {
        const c = candles[j];
        if (c.close < c.open) {
          obs.push({
            id:           `ob_bull_${j}`,
            type:         'bullish',
            top:          c.high,
            bottom:       c.low,
            midpoint:     (c.high + c.low) / 2,
            candleIdx:    j,
            ts:           c.ts,
            mitigated:    false,
            strength:     disp.volumeMultiple > 2 ? 'major' : 'minor',
            associatedBOS: false,
            description:  `Bullish OB at ${c.low.toFixed(2)}–${c.high.toFixed(2)}: last bearish candle before institutional buying displaced price upward`,
          });
          break;
        }
      }
    } else {
      for (let j = i - 1; j >= Math.max(0, i - 8); j--) {
        const c = candles[j];
        if (c.close > c.open) {
          obs.push({
            id:           `ob_bear_${j}`,
            type:         'bearish',
            top:          c.high,
            bottom:       c.low,
            midpoint:     (c.high + c.low) / 2,
            candleIdx:    j,
            ts:           c.ts,
            mitigated:    false,
            strength:     disp.volumeMultiple > 2 ? 'major' : 'minor',
            associatedBOS: false,
            description:  `Bearish OB at ${c.low.toFixed(2)}–${c.high.toFixed(2)}: last bullish candle before institutional selling displaced price downward`,
          });
          break;
        }
      }
    }
  }

  // Mark mitigated
  for (const ob of obs) {
    for (let j = ob.candleIdx + 1; j < candles.length; j++) {
      const c = candles[j];
      if (ob.type === 'bullish' && c.low <= ob.midpoint) { ob.mitigated = true; break; }
      if (ob.type === 'bearish' && c.high >= ob.midpoint) { ob.mitigated = true; break; }
    }
  }

  // Deduplicate nearby OBs, keep unmitigated, most recent first
  const fresh = obs
    .filter(ob => !ob.mitigated)
    .sort((a, b) => b.candleIdx - a.candleIdx);

  const deduped: OrderBlock[] = [];
  for (const ob of fresh) {
    const tooClose = deduped.some(d => Math.abs(d.midpoint - ob.midpoint) / ob.midpoint < 0.005);
    if (!tooClose) deduped.push(ob);
  }

  return deduped.slice(0, thresholds.maxOBs);
}
